import { useEffect, useRef, useState } from "react";
import * as api from "../api.js";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import EncaminharSetor from "../sobreposicoes/EncaminharSetor.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { ICONES, Icone } from "../visual.jsx";
import { MenuDeAcoes, Rosto } from "./comuns.jsx";

/**
 * Quem é este caso — preso no alto da coluna, e editável onde se lê.
 *
 * Era um bloco de 190px que rolava embora: foto de 66px, nome, telefone e um
 * botão "EDITAR CONTATO" que abria FORMULÁRIO — dois campos e dois botões por
 * cima da ficha para corrigir uma letra do nome. Duas coisas erradas: a
 * identidade é justamente o que não pode sair da tela (a coluna tem 2000px de
 * rolagem, e no meio da ficha ninguém sabia mais de quem era o holerite que
 * está conferindo), e formulário para dois campos é o caminho longo de uma
 * correção que se faz em três teclas.
 *
 * Agora o nome e o telefone SÃO os campos: clicar neles edita ali, Enter grava,
 * Esc desiste, sair do campo grava. É o mesmo gesto da ficha
 * (`CampoDaFicha.jsx`) — um vocabulário só de edição para a coluna inteira.
 */
export default function CabecalhoDoPainel({ caso, rolou, reuniao, fechar }) {
  const { caso: cheio } = useEstado();
  const { ajustar, ir } = useNavegacao();
  const pode = usePode();

  const podeEncaminhar = pode("registros:encaminhar");
  const haOutroSetor = useOutroSetor(podeEncaminhar);
  const [encaminhando, definirEncaminhando] = useState(false);
  const raiz = useRef(null);

  /* A altura deste cabeçalho, MEDIDA, vira variável na coluna: o gravador gruda
     embaixo dele e as seções param embaixo dele ao rolar. Era 74px escrito em
     dois lugares, e o cabeçalho muda de altura (o "Voltar para a ficha" da
     reunião aberta, a fonte, o zoom). */
  useEffect(() => {
    const el = raiz.current;
    const coluna = el?.parentElement;
    if (!el || !coluna || typeof ResizeObserver === "undefined") return undefined;
    const medir = () => coluna.style.setProperty("--altura-cabecalho-painel", `${el.offsetHeight}px`);
    medir();
    const olho = new ResizeObserver(medir);
    olho.observe(el);
    return () => olho.disconnect();
  }, []);

  const pasta = cheio?.pasta ?? null;
  /* O prazo mais próximo do caso, para a agenda abrir no DIA dele em vez de em
     "hoje" — é a pergunta que se faz olhando a pendência ("o que mais cai nesse
     dia?"). Sem prazo, a agenda abre onde ela abre. */
  const dia = String((cheio?.pendencias ?? []).find((p) => p.vence_em)?.vence_em ?? "").slice(0, 10) || null;

  const acoes = [
    /* "Transferir atendimento" saiu daqui: quem cuida se muda num lugar só, a
       fila de rostos ao lado, que abre a mesma caixa. */
    pode("documentos:ver") && pasta
      ? {
          chave: "pasta",
          rotulo: "Pasta no acervo",
          icone: ICONES.files,
          aoTocar: () => ir(`/documentos?pasta=${encodeURIComponent(pasta)}`),
        }
      : null,
    pode("registros:ver")
      ? {
          chave: "tarefas",
          rotulo: "Tarefas deste caso",
          icone: ICONES.tarefas,
          /* `?caso=` e não `?q=<nome>`: o filtro por `record_id` já existia em
             `/api/tarefas`, e casar por nome traria o homônimo do escritório
             vizinho. */
          aoTocar: () => ir(`/tarefas?caso=${caso.id}`),
        }
      : null,
    pode("registros:ver") && dia
      ? {
          chave: "agenda",
          rotulo: `Agenda de ${dia.split("-").toReversed().join("/")}`,
          icone: ICONES.agenda,
          aoTocar: () => ir(`/tarefas?visao=dia&de=${dia}&ate=${dia}`),
        }
      : null,
    /* Só com outro setor para onde ir: com um setor só o item seria uma caixa
       com a lista de destinos vazia. */
    podeEncaminhar && haOutroSetor
      ? {
          chave: "encaminhar",
          rotulo: "Encaminhar a outro setor",
          icone: ICONES.enviar,
          aoTocar: () => definirEncaminhando(true),
        }
      : null,
  ].filter(Boolean);

  return (
    <div
      ref={raiz}
      className={`sticky top-0 z-[3] bg-secondary/95 px-4 pt-3 pb-2.5 backdrop-blur-[6px] ${
        rolou ? "border-b border-b-border" : ""
      }`}
    >
      {/* Voltar da reunião aberta: a coluna é uma PILHA, e toda pilha precisa de
          degrau para trás — sem ele o único caminho de volta seria o botão do
          navegador, que também fecharia o painel. */}
      {reuniao ? (
        <button
          type="button"
          onClick={() => ajustar({ gravacao: null, painel: "1" })}
          className="alvo mb-2 flex cursor-pointer items-center gap-1.5 rounded-controle border-none bg-transparent p-0 text-apoio font-semibold text-tinta-suave hover:text-foreground"
        >
          <Icone de={ICONES.voltar} tamanho={16} />
          Voltar para a ficha
        </button>
      ) : null}

      {/*
       * A IDENTIDADE saiu daqui (27/08) — nome, telefone e etapa.
       *
       * Os três estavam escritos de novo a 300px de distância, no topo da
       * conversa, e este cabeçalho era a segunda vez que a tela dizia com quem se
       * está falando. Pior: o nome aqui era o EDITÁVEL e o de lá o grande, então
       * corrigir "BONFIN" para "BONFIM" exigia atravessar a tela para mexer na
       * cópia menor. Agora o cabeçalho da conversa edita os dois no lugar onde
       * eles estão escritos, e a etapa é o trilho — que também move.
       *
       * O que sobra é o que é DESTE cabeçalho: quem cuida do caso e o menu de
       * ações dele. O painel ficou com uma faixa de 44px onde tinha três.
       */}
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-selo font-bold tracking-[.08em] text-tinta-fraca">QUEM CUIDA</span>
        <div className="flex flex-none items-center gap-0.5">
          <QuemCuida caso={caso} />
          {/* Sem item nenhum não há menu: um ⋮ que abre vazio é botão mentindo. */}
          {acoes.length ? (
            <MenuDeAcoes
              rotulo={`Ações do atendimento de ${caso.nome}`}
              titulo="As outras telas deste caso"
              largura="240px"
              ancora="bottom end"
              itens={acoes}
              /* 36, o botão de ícone do cabeçalho — a mesma medida do ✕ ao lado.
                 Aberto fica escuro: é o botão cujo menu está na tela. */
              classe="flex size-padrao cursor-pointer items-center justify-center rounded-controle border-none bg-transparent text-tinta-suave hover:bg-foreground/8 hover:text-foreground data-popup-open:bg-escuro data-popup-open:text-sobre-escuro"
            >
              <Icone de={ICONES.mais} tamanho={16} />
            </MenuDeAcoes>
          ) : null}
          {fechar}
        </div>
      </div>
      {encaminhando ? <EncaminharSetor record_id={caso.id} nome={caso.nome} aoFechar={() => definirEncaminhando(false)} /> : null}
    </div>
  );
}

/* Os destinos mudam quando alguém cria um setor, não entre um caso e outro: uma
   ida por aba basta, e não uma por caso aberto. */
let destinos = null;
function useOutroSetor(pode) {
  const [ha, definirHa] = useState(false);
  useEffect(() => {
    if (!pode) return undefined;
    let vivo = true;
    destinos ??= api.destinosDeEncaminhar().then(
      (r) => (r.destinos ?? []).length > 0,
      () => {
        destinos = null;
        return false;
      },
    );
    destinos.then((v) => vivo && definirHa(v));
    return () => {
      vivo = false;
    };
  }, [pode]);
  return pode && ha;
}

/**
 * Quem cuida do atendimento: o titular e quem entrou junto.
 *
 * O caso tinha UM dono e o escritório não trabalha assim — o sócio entra na
 * audiência, o estagiário cobra documento, e os dois precisavam do caso na
 * pastilha "Meus". O titular continua sendo um só (é ele que a régua cobra e o
 * fluxo escala); os outros são acompanhantes, e a fila de retratos diz isso sem
 * uma palavra: o titular tem anel, os outros não.
 *
 * Fila de retratos de 28px, e não uma frase: "sem titular — assumir" escrito por
 * extenso ocupava a largura inteira do cabeçalho para dizer o que um círculo
 * tracejado diz — e era essa linha que empurrava o resto da ficha para baixo da
 * primeira dobra.
 */
function QuemCuida({ caso }) {
  const { caso: cheio, usuario } = useEstado();
  const { assumirCaso } = useAcoes();
  const { ajustar } = useNavegacao();
  const responsavel = caso?.responsavel ?? null;
  const meu = Boolean(usuario?.pessoa && responsavel?.toUpperCase() === usuario.pessoa.toUpperCase());
  const acompanhantes = cheio?.acompanhantes ?? [];
  /* Titular primeiro, os outros na ordem em que entraram — a mesma ordem do
     domínio, onde o primeiro é quem a régua cobra. */
  const cuidam = [...(responsavel ? [responsavel] : []), ...acompanhantes.map((a) => a.pessoa)];
  const abrirCaixa = () => ajustar({ transferir: true });

  /*
   * UMA superfície para mexer em quem cuida: a caixa.
   *
   * Aqui havia um menu próprio — "IAN passa a acompanhar", "CAMILA assume" — e o
   * clique num rosto TIRAVA a pessoa do caso. Somando com o item "Transferir
   * atendimento" do menu ao lado e o campo `colaborador` da ficha, o escritório
   * tinha três caminhos diferentes para a mesma decisão, cada um com um
   * vocabulário ("acompanhar", "assumir", "transferir") e regras diferentes: o
   * daqui acrescentava, o de lá substituía, e o da ficha trocava só o primeiro.
   *
   * Agora qualquer toque nesta fila abre a caixa, que mostra o conjunto inteiro,
   * a carga de cada um e quem sai — e é a única coisa que grava. A fila aqui
   * VOLTOU a ser o que ela é: a resposta a "quem está neste caso", de relance.
   *
   * A exceção é ASSUMIR, e ela se justifica: caso sem titular na fila é um toque
   * ("é meu"), não uma escolha entre nove nomes. Abrir uma caixa para dizer o
   * próprio nome é o caminho longo para a decisão mais frequente do dia.
   */
  /*
   * COM A IA NO COMANDO, ela é o primeiro rosto.
   *
   * A fila mostrava o titular humano com o anel de "responde pelo atendimento"
   * exatamente enquanto quem respondia era a IA — a informação mais importante
   * do caso (quem está falando com o cliente agora) só existia na faixa preta,
   * que rola para fora da tela. O rosto verde com o emoji do agente responde
   * isso de relance, e o toque abre o escopo da delegação, que é onde se ajusta
   * ou se retoma.
   *
   * O humano continua ao lado, sem anel: ele é para quem o caso VOLTA.
   */
  const delegacao = cheio?.delegacao ?? null;
  const nomeDaIA = delegacao?.agente_nome || "A IA do escritório";

  return (
    <span className="flex items-center gap-[3px] pr-0.5">
      {delegacao ? (
        <button
          type="button"
          onClick={() => ajustar({ delegar: true })}
          data-dica={`${nomeDaIA} está conduzindo — toque para ajustar o escopo ou retomar${
            responsavel ? `. Volta para ${responsavel}` : ""
          }`}
          aria-label={`No comando: ${nomeDaIA}. Ajustar ou retomar o atendimento`}
          /* O mesmo símbolo da lista e do trilho — quem responde agora não é
             gente, e isso se diz com um ícone só, não com o emoji que cada
             persona escolheu. */
          className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-full border-none bg-verde text-sobre-terra ring-2 ring-verde-vivo ring-offset-1 ring-offset-secondary"
        >
          <Icone de={ICONES.agenteIa} tamanho={16} />
        </button>
      ) : null}
      {cuidam.length ? (
        /*
         * UM alvo, com as CARAS e o NOME escrito.
         *
         * Eram três círculos de 22px com as iniciais, um botão cada: "F", "C",
         * "I" ao lado de uma conversa cheia de rostos de verdade. Duas coisas
         * estavam erradas ali. A primeira é que a foto EXISTE — as cinco pessoas
         * do escritório têm avatar gravado, e a fila desenhava letra porque
         * nunca olhou para ele. A segunda é que letra não é nome: decifrar "C"
         * como Camila é trabalho, e a resposta a "quem cuida deste caso" tem de
         * ser lida, não adivinhada.
         *
         * Agora é uma peça só: as caras empilhadas (o idioma de grupo, com
         * sobreposição para caber), o PRIMEIRO NOME de quem responde escrito ao
         * lado e "+N" quando há mais. Alvo único porque a ação é a mesma nos
         * três — qualquer toque abre a caixa —, e três botões vizinhos de 22px
         * são três chances de errar para a mesma tela.
         */
        <button
          type="button"
          onClick={abrirCaixa}
          data-dica={
            delegacao
              ? `${cuidam[0]} recebe o atendimento de volta quando a IA devolver — clique para mudar quem cuida`
              : cuidam.length > 1
                ? `${cuidam.join(", ")} — ${cuidam[0]} responde pelo atendimento. Clique para mudar.`
                : `${cuidam[0]} responde pelo atendimento — clique para mudar quem cuida`
          }
          aria-label={`Quem cuida: ${cuidam.join(", ")}. Mudar quem cuida deste atendimento`}
          className="alvo flex min-w-0 flex-none cursor-pointer items-center gap-1.5 rounded-full border-none bg-transparent p-0 pr-0.5"
        >
          <span className="flex flex-none items-center">
            {cuidam.slice(0, 3).map((nome, i) => (
              <span
                key={nome}
                /* Sobreposição de 8px: três caras de 28 em fila comem 90px do
                   cabeçalho, empilhadas comem 68 — e a pilha é o que faz ler
                   como "um grupo" em vez de "três coisas". `zIndex` decrescente
                   para o primeiro (quem responde) ficar por cima. */
                className={`flex-none rounded-full ring-2 ring-secondary ${i ? "-ml-2" : ""} ${
                  /* Com a IA no comando ninguém leva o anel: o anel diz "é quem
                     responde agora", e agora quem responde é ela. */
                  i === 0 && !delegacao ? "ring-verde-fundo" : ""
                }`}
                style={{ zIndex: 3 - i }}
              >
                <Rosto pessoa={nome} tamanho={28} />
              </span>
            ))}
          </span>
          {/* O nome de QUEM RESPONDE, escrito. Só o primeiro nome: "JOÃO VICTOR"
              inteiro come a faixa, e é o primeiro que se fala no escritório. */}
          <span className="min-w-0 truncate text-apoio leading-none font-bold text-tinta-media">
            {String(cuidam[0]).split(/\s+/)[0]}
            {cuidam.length > 1 ? <span className="font-normal text-tinta-suave">{` +${cuidam.length - 1}`}</span> : null}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={assumirCaso}
          data-dica="Sem ninguém — clique para assumir este atendimento"
          aria-label="Assumir este atendimento"
          className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-full border border-dashed border-terra/50 bg-transparent text-apoio font-bold text-terra"
        >
          ?
        </button>
      )}

      {/* Assumir continua a UM toque quando o caso não é meu: é a decisão mais
          frequente do dia e não precisa de caixa. */}
      {responsavel && !meu ? (
        <button
          type="button"
          onClick={assumirCaso}
          data-dica="Assumir este atendimento — você passa a ser o titular"
          aria-label="Assumir este atendimento"
          className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-full border border-dashed border-verde-fundo/45 bg-transparent text-apoio font-bold text-verde-fundo"
        >
          ✓
        </button>
      ) : null}

      {/* O "+" só existe quando não há rosto para tocar: com a pilha na tela,
          ele era a segunda porta para a mesma caixa, a 4px da primeira. */}
      {cuidam.length ? null : (
        <button
          type="button"
          onClick={abrirCaixa}
          data-dica="Escolher quem cuida deste atendimento — uma pessoa ou várias"
          aria-label="Escolher quem cuida deste atendimento"
          className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-full border border-dashed border-borda-controle bg-transparent text-corpo leading-none font-bold text-tinta-suave"
        >
          +
        </button>
      )}
    </span>
  );
}

/* O `Pessoa` de INICIAIS morreu em 09/09.
   Ele desenhava um círculo de 22px com `iniciais(nome)` e a cor tirada do nome —
   e as cinco pessoas do escritório têm foto gravada no banco desde agosto. A
   fila passou a usar o `Avatar` da casa, que mostra a foto e cai nas iniciais só
   quando ela não existe, que é o que o resto da mesa já fazia. */
